import { ENVS, Env } from '@adsk/acs-sc-web-platform-api';
import Badge from '@adsk/alloy-react-badge';

import { ItemState, Priority } from './types';

export const ASSIGNEE_PICKER_COMPANY = 'company';
export const ASSIGNEE_PICKER_ROLE = 'role';
export const ASSIGNEE_PICKER_USER = 'user';

export const SUBMITTAL_ENTITIY_TYPE = 'submittalitem';
export const SUBMITTAL_ENTITIY_DOMAIN = 'autodesk-construction-submittals';

export const ENV_CONFIGS: Record<Env, Record<string, string>> = {
  [ENVS.QA]: {
    listItemsPath:
      'submittals-qa/{version}/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals-qa/{version}/projects/{projectId}/specs',
    customAttributes:
      'submittals-qa/{version}/projects/{projectId}/settings/custom-attributes',
  },
  [ENVS.STAGING]: {
    listItemsPath:
      'submittals/{version}/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals/{version}/projects/{projectId}/specs',
    customAttributes:
      'submittals/{version}/projects/{projectId}/settings/custom-attributes',
  },
  [ENVS.PRODUCTION]: {
    listItemsPath:
      'submittals/{version}/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals/{version}/projects/{projectId}/specs',
    customAttributes:
      'submittals/{version}/projects/{projectId}/settings/custom-attributes',
  },
};

export const PAGE_SIZE = 50;

export const VERSION_1 = 'v1';
export const VERSION_2 = 'v2';

export const SUBMITTALS_CUSTOM_ATTRIBUTES_FF =
  'submittals-custom-attributes-fe';

export const REVIEWER_ASSIGNEE_TYPE: Record<string, string> = {
  user: '1',
  company: '2',
};

interface StatusMetadata {
  name: string;
  color: string;
  statusText: string;
  stateText?: string;
}
export const REVIEW_STATE = 'rev';
export const IN_PROGRESS_STEP = 'in-progress';

export const ASSIGNEE_TYPES: Record<string, string> = {
  USER: 'user',
  COMPANY: 'company',
  ROLE: 'role',
};

export const statuses: Record<ItemState, StatusMetadata> = {
  draft: {
    name: 'draft',
    statusText: 'STATUS_DRAFT',
    color: Badge.COLORS.GRAY,
  },
  'sbc-1': {
    name: 'required',
    stateText: 'STATE_SBC1',
    statusText: 'STATUS_REQUIRED',
    color: Badge.COLORS.BLUE,
  },
  'mgr-1': {
    name: 'open',
    stateText: 'STATE_MGR1',
    statusText: 'STATUS_OPEN',
    color: Badge.COLORS.YELLOW,
  },
  rev: {
    name: 'rev',
    stateText: 'STATE_REV',
    statusText: 'STATUS_OPEN',
    color: Badge.COLORS.YELLOW,
  },
  'mgr-2': {
    name: 'mgr-2',
    stateText: 'STATE_MGR2',
    statusText: 'STATUS_OPEN',
    color: Badge.COLORS.YELLOW,
  },
  'sbc-2': {
    name: 'sbc-2',
    statusText: 'STATUS_CLOSED',
    color: Badge.COLORS.GRAY_200,
  },
  void: {
    name: 'void',
    statusText: 'STATUS_VOID',
    color: Badge.COLORS.DEFAULT,
  },
};

// export const priorities = ['High', 'Normal', 'Low'];

interface PriorityData {
  label: string;
  value: string;
}

export const priorities: Record<Priority, PriorityData> = {
  High: {
    label: 'HIGH',
    value: 'High',
  },
  Normal: {
    label: 'NORMAL',
    value: 'Normal',
  },
  Low: {
    label: 'LOW',
    value: 'Low',
  },
};

export const SORT_ORDER = {
  ASC: 'asc',
  DESC: 'desc',
};

export const VIRTUAL_ROW_HEIGHT = 48;

export const VIRTUAL_OVER_SCAN = 3;

export const ENTITIES_MAPPER_KEY = 'id';

export const TABLE_COLUMN_ACCESSOR_KEY = {
  ID: 'id',
  STEPS: 'steps',
  IDENTIFIER: 'identifier',
  STATE_ID: 'stateId',
  REVISION: 'revision',
  TITLE: 'title',
  BALL_IN_COURT_USERS: 'ballInCourtUsers',
  PENDING_BALL_IN_COURT: 'pendingBallInCourt',
  DUE_DATE: 'dueDate',
  PACKAGE: 'package',
  CUSTOM_IDENTIFIER: 'customIdentifierHumanReadable',
  SPEC: 'spec',
  PRIORITY: 'priority',
};

export const TABLE_COLUMN_ID_KEY = {
  SPEC: 'spec.identifier',
  PACKAGE: 'package.title',
  CUSTOM_IDENTIFIER: 'customIdentifier',
  SELECT: 'select',
};

export const MULTI_SELECT_DISPLAY_LIMIT = 10;
export const EMPTY_VALUE = '-';

export const CustomAttributesTypes = {
  TEXT: 'autodesk.spec:spec.string',
  // eslint-disable-next-line spellcheck/spell-checker
  LONG_TEXT: 'autodesk.spec.aec:multilineText',
  // eslint-disable-next-line spellcheck/spell-checker
  NUMBER: 'autodesk.spec.aec:number',
  BOOLEAN: 'autodesk.spec:spec.bool',
  DATE: 'autodesk.spec:spec.date',
  // eslint-disable-next-line spellcheck/spell-checker
  SINGLE_SELECT: 'autodesk.spec.aec:singleSelect',
  // eslint-disable-next-line spellcheck/spell-checker
  MULTI_SELECT: 'autodesk.spec.aec:multiSelect',
};

export const CUSTOM_ATTRIBUTE_PREFIX = 'custom_attribute:';
export const CUSTOM_ATTRIBUTE_LIMIT = 100;
export const CUSTOM_ATTRIBUTE_OFFSET = 0;
export const CUSTOM_ATTRIBUTES_URL_IDENTIFIER = 'customAttributes';

export const CUSTOM_FIELDS_CELL_STYLES = {
  [CustomAttributesTypes.TEXT]: {
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
    'white-space': 'normal' /* Allows the text to wrap */,
  },
  [CustomAttributesTypes.NUMBER]: {
    display: '-webkit-box',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden' /* Hide overflowed content */,
    'white-space': 'nowrap' /* Prevent text from wrapping */,
    'text-overflow': 'ellipsis',
  },
  [CustomAttributesTypes.MULTI_SELECT]: {
    display: '-webkit-box',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
    'white-space': 'normal' /* Allows the text to wrap */,
  },
};
